import { Link } from "react-router-dom";
import { Lock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { STATUS_STYLES } from "@/lib/ui";

export const StatusPill = ({ status, testId }) => (
  <span data-testid={testId} className={`inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${STATUS_STYLES[status] || STATUS_STYLES.Event}`}>
    {status}
  </span>
);

export const PageHeader = ({ eyebrow, title, subtitle, children }) => {
  const { user } = useAuth() || {};
  const viewer = user?.workspace?.role === "viewer";
  return (
  <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-8 rise">
    <div>
      {eyebrow && <div className="eyebrow mb-2">{eyebrow}</div>}
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="page-title">{title}</h1>
      {subtitle && <p className="text-sm sm:text-base text-muted-foreground mt-2 max-w-2xl">{subtitle}</p>}
    </div>
    {children && !viewer && <div className="flex flex-wrap gap-2">{children}</div>}
  </div>
  );
};

export const Loading = () => (
  <div className="flex items-center justify-center py-24 text-muted-foreground" data-testid="loading-state">
    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
  </div>
);

export const ErrorBox = ({ error }) => (
  <div className="surface p-6 text-sm text-red-500" data-testid="error-state">{error}</div>
);

export const UpgradeGate = ({ gated }) => (
  <div className="surface p-10 max-w-xl mx-auto text-center rise" data-testid="upgrade-gate">
    <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4"><Lock className="h-5 w-5" /></div>
    <h2 className="text-xl font-semibold">Upgrade to unlock this module</h2>
    <p className="text-sm text-muted-foreground mt-2">{gated?.message}</p>
    <p className="text-xs mt-3 text-emerald-600 dark:text-emerald-400 font-medium" data-testid="upgrade-gate-recommended">
      {["portfolio", "team", "advanced_ai", "cos_advanced"].includes(gated?.feature)
        ? "Available on Operator · $99/month. Includes the multi-company dashboard, portfolio health score and team access."
        : gated?.feature === "cos" ? "Recommended: Professional · $49/month. Includes the AI Chief of Staff, Deals pipeline and auto-filed receipts."
        : "Recommended: Professional · $49/month. Includes Tax Tracking, Records Vault, AI Assistant and Revival."}
    </p>
    <Button asChild className="mt-6" data-testid="upgrade-gate-cta"><Link to="/app/billing">View plans</Link></Button>
  </div>
);

export const PageState = ({ state, children }) => {
  if (state.loading) return <Loading />;
  if (state.gated) return <UpgradeGate gated={state.gated} />;
  if (state.error) return <ErrorBox error={state.error} />;
  return children;
};

export const Empty = ({ icon: Icon, title, text, children, testId }) => (
  <div className="surface border-dashed p-10 text-center" data-testid={testId}>
    {Icon && <Icon className="h-8 w-8 mx-auto text-muted-foreground mb-3" />}
    <div className="font-semibold">{title}</div>
    {text && <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">{text}</p>}
    {children && <div className="mt-5 flex justify-center gap-2 flex-wrap">{children}</div>}
  </div>
);

export const Field = ({ label, children }) => (
  <label className="block space-y-1.5">
    <span className="text-xs font-medium text-muted-foreground">{label}</span>
    {children}
  </label>
);
